import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  Autosuggest,
  type AutosuggestChangeEvent,
  type AutosuggestInputProps,
  type AutosuggestProps,
  fonts,
} from 'folio-common-components';
import { formatters } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { lazyWithPreload } from 'react-lazy-with-preload';
import { connect } from 'react-redux';
import { BuildingIcon } from '../../icons';
import type { Dispatch, RootState } from '../../state';
import {
  type CompanySearchItem,
  selectors as companySearchSelectors,
  thunks as companySearchThunks,
} from '../../state/companySearch';
import { isAllowedOwnerOrgType } from '../../utils';
import { FormRow } from '../FormRow';
import { Label } from '../label';

const PostalPlace = lazyWithPreload(() => import('../PostalPlace'));

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1em;
`;

const IconWrapper = styled.div`
  margin-right: 0.5em;
`;

const ItemWrapper = styled.div`
  font-size: 0.8em;
`;

function renderSelectedCompanyItem(
  item: Pick<CompanySearchItem, 'orgId' | 'name'>,
) {
  return (
    <SelectedItem>
      <IconWrapper>
        <BuildingIcon />
      </IconWrapper>
      <p>
        <b>
          {item.name} (org.nr.: {formatters.formatOrgNumber(item.orgId)})
        </b>
      </p>
    </SelectedItem>
  );
}

export const OrgItem: React.FC<{ item: CompanySearchItem }> = ({ item }) => {
  return (
    <div>
      <div>
        <b>{item.name}</b>
      </div>
      <ItemWrapper>
        Org.nr.: {formatters.formatOrgNumber(item.orgId)}
        {item.street && item.postalCode ? (
          <>
            {' '}
            <span aria-hidden={true}>•</span> {item.street}, {item.postalCode}{' '}
            <React.Suspense fallback={null}>
              <PostalPlace postalCode={item.postalCode} />
            </React.Suspense>
          </>
        ) : null}
      </ItemWrapper>
    </div>
  );
};

function itemMarkup(item: CompanySearchItem) {
  const isAllowed = isAllowedOwnerOrgType(item.incorporation);
  return !isAllowed ? (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      onClick={event => event.stopPropagation()}
      aria-disabled={true}
      css={css`
        color: ${colors.wcagGray};
      `}
    >
      <div
        css={css`
          ${fonts.font200medium}
        `}
      >
        {item.name}
      </div>
      <ItemWrapper>{item.incorporation} kan ikke være eier</ItemWrapper>
    </div>
  ) : (
    <OrgItem item={item} />
  );
}

interface Props {
  suggestions: CompanySearchItem[];
  selected?: Pick<CompanySearchItem, 'orgId' | 'name'>;
  value: string;
  onSelect: (company: CompanySearchItem | undefined) => any;
  onChange: (val: string | undefined) => any;
  onSearch: (term: string) => any;
  renderSuggestion?: AutosuggestProps<any>['renderSuggestion'];
}

const BaseControlledCompanyInput: React.FC<Props> = props => {
  const {
    onChange,
    onSelect,
    selected,
    value,
    suggestions,
    onSearch,
    renderSuggestion,
  } = props;

  const inputProps: AutosuggestInputProps<any> = {
    value: selected ? selected.name : value,
    onChange: (_: any, data: AutosuggestChangeEvent) => {
      if (data.method === 'type') {
        onSelect(undefined);
        onChange(data.newValue);
      }
    },
    spellCheck: false,
    css: css`
      width: 100%;
    `,
    // autoFocus: true,
    id: 'currentCompany',
    autoComplete: 'off',
  };

  return (
    <>
      <FormRow>
        <Label htmlFor="currentCompany">Selskapsnavn eller org.nr.</Label>
        <Autosuggest<CompanySearchItem>
          suggestions={selected ? [] : suggestions.slice(0, 5)}
          onSuggestionsClearRequested={() => undefined}
          onSuggestionsFetchRequested={({ value }) => {
            onSearch(value);
          }}
          onSuggestionSelected={(_value, { suggestion }) => {
            onSelect(suggestion);
          }}
          shouldRenderSuggestions={value => value.trim().length > 2}
          getSuggestionValue={item => item.name}
          renderSuggestion={renderSuggestion ?? itemMarkup}
          inputProps={inputProps}
          id="currentCompany"
        />
      </FormRow>
      {selected && renderSelectedCompanyItem(selected)}
    </>
  );
};

// fixme
function mapStateToProps(state: RootState, ownProps: { value: string }) {
  return {
    suggestions: companySearchSelectors.getItems(state, ownProps.value),
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      onSearch: companySearchThunks.searchForCompany,
    },
    dispatch,
  );
}

export const ControlledCompanyInput = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseControlledCompanyInput);
